import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { animated, config, useSpring } from "react-spring";

import OttoPeek from "../../../assets/imgs/otto-peek.svg";
import ScribblesPeek from "../../../assets/imgs/scribbles-peek.svg";
import { alert } from "../../common/Alert";
import { DSButton, DSCardWrapper } from "../molecules";

// Track how many modals are currently open to manage body scroll lock
let openModalCount = 0;

export interface DSModalAction {
  label: string;
  variant?: "primary" | "secondary" | "cancel";
  onClick: () => void;
  disabled?: boolean;
  dataTestId?: string;
}

export interface DSModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Called when modal should close (X button or backdrop click) */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Optional subtitle (smaller text below title) */
  subtitle?: string;
  /** Modal body content */
  children: React.ReactNode;
  /** Action buttons to display in the footer. If not provided, modal is informational-only (no X button, no backdrop close) */
  actions?: DSModalAction[];
  /** Explicitly control whether the X button and backdrop close are shown. Defaults to true when actions are present, false otherwise. */
  dismissible?: boolean;
  /** Disable closing the modal by clicking the backdrop, while still allowing the X button. Defaults to false. */
  disableBackdropClose?: boolean;
  /** Optional width override (default: w-[50vw] max-w-[600px]) */
  width?: string;
  dataTestId: string;
}

/**
 * DSModal - A reusable modal dialog component
 *
 * Features:
 * - Backdrop overlay with click-to-close (only when actions are provided)
 * - Customizable title, subtitle, and body
 * - Flexible action buttons in footer
 * - Portal rendering for proper z-index layering
 * - Focuses modal on open for accessibility
 * - Otto and Scribbles mascots peeking over the top
 * - Automatically infers modal type:
 *   - With actions: Interactive modal (X button, backdrop close, action footer)
 *   - Without actions: Informational modal (no X button, no backdrop close, no footer)
 */
export const DSModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  actions,
  dismissible,
  disableBackdropClose = false,
  width = "w-[50vw] max-w-[600px]",
  dataTestId,
}: DSModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Infer modal type from presence of actions, overridable via dismissible prop
  const hasActions = actions && actions.length > 0;
  const isInteractive = dismissible ?? hasActions;

  // Animation
  const modalAnim = useSpring({
    config: config.stiff,
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "scale(1)" : "scale(0.8)",
  });

  // Lock body scroll when modal is open (supports stacked modals)
  useEffect(() => {
    if (isOpen) {
      openModalCount++;
      document.body.style.overflow = "hidden";
    }

    return () => {
      if (isOpen) {
        openModalCount--;
        if (openModalCount === 0) {
          document.body.style.overflow = "";
        }
      }
    };
  }, [isOpen]);

  // Focus modal when opened for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Handle backdrop click - only allow for interactive modals
  const handleBackdropClick = (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!isInteractive || disableBackdropClose) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-lg bg-black/80"
      onClick={handleBackdropClick}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      <animated.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={`relative ${width} max-h-[90vh] flex flex-col focus:outline-none`}
        style={modalAnim}
      >
        {/* Otto Mascot */}
        <>
          <div
            onClick={() => alert("dark", "BOOP!")}
            className="absolute z-40 w-4 h-4 cursor-pointer top-[-22px] left-[94px]"
          />
          <img
            className="absolute z-30 top-[-50px] left-[40px] w-[120px]"
            src={OttoPeek}
            alt="Otto"
          />
        </>

        {/* Scribbles Mascot */}
        <img
          className="absolute z-30 top-[-43px] right-[40px] w-[80px]"
          src={ScribblesPeek}
          alt="Scribbles"
        />

        <DSCardWrapper
          padding="p-0"
          variant="modal"
          dataTestId={`${dataTestId}-card`}
        >
          {/* Header */}
          <div className="relative p-md shrink-0">
            <div className="flex flex-col gap-sm items-end">
              {/* Close Button - only show for interactive modals */}
              {isInteractive && (
                <button
                  onClick={onClose}
                  className="shrink-0 w-3 h-3 flex items-center justify-center text-primary hover:text-secondary transition-colors"
                  aria-label="Close modal"
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="w-3 h-3"
                  />
                </button>
              )}

              {/* Title */}
              <h2 id="modal-title" className="w-full">
                {title}
              </h2>

              {/* Subtitle */}
              {subtitle && <p className="w-full">{subtitle}</p>}
            </div>
          </div>

          {/* Body */}
          <div className="px-md pb-md overflow-y-auto min-h-0">
            <div className="text-primary">{children}</div>
          </div>

          {/* Footer with Actions - only show for interactive modals */}
          {hasActions && (
            <div className="bg-secondary rounded-b-large p-md flex items-center justify-between gap-md shrink-0">
              {/* Left side - Cancel actions */}
              <div className="flex gap-xs">
                {actions
                  .filter((action) => action.variant === "cancel")
                  .map((action, index) => (
                    <DSButton
                      key={index}
                      variant="cancel"
                      size="md"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      dataTestId={
                        action.dataTestId ||
                        `${dataTestId}-cancel-${index}`
                      }
                    >
                      {action.label}
                    </DSButton>
                  ))}
              </div>

              {/* Right side - Primary/Secondary actions */}
              <div className="flex gap-xs">
                {actions
                  .filter((action) => action.variant !== "cancel")
                  .map((action, index) => (
                    <DSButton
                      key={index}
                      variant={action.variant || "primary"}
                      size="md"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      dataTestId={
                        action.dataTestId ||
                        `${dataTestId}-${action.variant || "primary"}-${index}`
                      }
                    >
                      {action.label}
                    </DSButton>
                  ))}
              </div>
            </div>
          )}
        </DSCardWrapper>
      </animated.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

DSModal.displayName = "DSModal";
